<template>
  <BaseModal :modalTitle="title">
    <div class="mb-3 min-h-20">
      <p>{{ bodyText }}</p>
    </div>

    <template #buttons>
      <UiBaseButton
        :text="$t('action.cancel')"
        theme="neutral"
        @button-click="onCancelClick" />
      <UiBaseButton
        :text="$t('W: Fortsæt')"
        theme="primary"
        @button-click="onConfirmClick" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'

/**
 * Confirms a change that discards the customer's own price overrides — moving
 * them to a different pricing segment, or to a different currency.
 *
 * Both cases are the same decision: the overrides were negotiated against one
 * base, and after the change that base is gone. Keeping them was previously
 * offered for the currency case and has been removed deliberately — a kept
 * override silently means something different afterwards (an amount reinterpreted
 * in a new currency, or a discount measured against a different segment price),
 * which is worse than losing it visibly.
 *
 * `onCancel` runs on **any** dismissal — the button, Escape, or the X — because
 * the value has already changed by the time this opens, so cancelling has to put
 * it back. That is why this is not the shared ConfirmationModal, which only
 * calls onCancel from its own Cancel button.
 */
export default {
  name: 'DiscardCustomPricesModal',

  emits: ['close'],

  props: {
    /** Which change is being confirmed — decides the wording only. */
    changeType: {
      type: String as PropType<'segment' | 'currency'>,
      required: true,
    },
    onConfirm: {
      type: Function,
      required: true,
    },
    onCancel: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      actionTaken: false,
    }
  },

  computed: {
    title(): string {
      return this.changeType === 'segment'
        ? this.$t('W: Skift prissegment')
        : this.$t('W: Skift valuta')
    },

    bodyText(): string {
      return this.changeType === 'segment'
        ? this.$t(
            'W: Du har tilpassede priser — disse vil gå tabt, når du skifter prissegment.'
          )
        : this.$t(
            'W: Du har tilpassede priser — disse vil gå tabt, når du skifter valuta.'
          )
    },
  },

  methods: {
    onConfirmClick() {
      this.actionTaken = true
      this.onConfirm()
      this.$emit('close')
    },
    onCancelClick() {
      // Marked as an action for the same reason confirm is: without it the
      // unmount hook below sees `actionTaken === false` and calls onCancel a
      // second time, so the Cancel button double-reverts.
      this.actionTaken = true
      this.onCancel?.()
      this.$emit('close')
    },
  },

  beforeUnmount() {
    // Escape and the X close the modal without either button being pressed;
    // without this the change would stand while the user believed they backed
    // out. Both buttons set `actionTaken`, so this covers only those dismissals
    // and onCancel still runs exactly once per dismissal.
    if (!this.actionTaken) {
      this.onCancel?.()
    }
  },
}
</script>
